import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MessagesSquare, ChevronLeft, ChevronRight, TrendingUp, User, Headset } from "lucide-react";

const STOP = new Set(("a,an,the,hai,hain,ho,he,ki,ke,ko,ka,kya,me,mein,par,se,ya,aur,ye,yeh,wo,woh,to,hi,bhi,nahi,hai?,is,it,of,in,on,for,and,or,at,by,with,about,into,through,during,before,after,above,below,from,up,down,out,off,over,under,again,further,then,once,here,there,when,where,why,how,all,any,both,each,few,more,most,other,some,such,no,nor,not,only,own,same,so,than,too,very,can,will,just,should,now,main,mujhe,muje,mera,mere,ap,aap,hum,kar,karna,karo,kare,karein,kiya,raha,rahe,sakta,sakte,tha,thi,the,tha?,batao,bataye,bata,dijiye,chahiye,ek,kuch,koi,sab,us,is,ina,un,jo,jab,agar,lekin,wala,wali,please,pls,thank,thanks,ok,okay,hmm,haan,nahi").split(","));

function topTopics(msgs, n = 8) {
  const freq = {};
  for (const m of msgs) {
    if (m.role !== "user") continue;
    const words = (m.content || "").toLowerCase().replace(/[^a-zA-Z\u0900-\u097F\s]/g, " ").split(/\s+/);
    const seen = new Set();
    for (let w of words) {
      if (w.length < 4 || STOP.has(w) || seen.has(w)) continue;
      seen.add(w);
      freq[w] = (freq[w] || 0) + 1;
    }
  }
  return Object.entries(freq).sort((a, b) => b[1] - a[1]).slice(0, n).map(([word, count]) => ({ word, count }));
}

export default function ChatLogs() {
  const now = new Date();
  const [month, setMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`);
  const [data, setData] = useState(null);
  const [active, setActive] = useState(null);

  useEffect(() => {
    setData(null);
    setActive(null);
    api.get("/chat-logs", { params: { month } }).then(({ data }) => setData(data));
  }, [month]);

  const months = useMemo(() => {
    const arr = [];
    const d = new Date();
    for (let i = 0; i < 6; i++) {
      arr.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
      d.setMonth(d.getMonth() - 1);
    }
    return arr;
  }, []);

  const topics = useMemo(() => (data ? topTopics(data.messages) : []), [data]);
  const sessions = data?.sessions || [];
  const activeMsgs = useMemo(
    () => (data && active ? data.messages.filter((m) => m.session_id === active.session_id) : []),
    [data, active]
  );
  const monthLabel = (m) => new Date(m + "-02").toLocaleDateString("en-IN", { month: "long", year: "numeric" });

  return (
    <div className="space-y-5" data-testid="chat-logs-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight text-slate-900 flex items-center gap-2">
            <MessagesSquare className="w-6 h-6 text-emerald-700" /> Website Chat Logs
          </h1>
          <p className="text-sm text-slate-500">Customers ne chat par kya-kya pucha — mahine ka analysis aur poori conversations.</p>
        </div>
        <Select value={month} onValueChange={setMonth}>
          <SelectTrigger className="w-44 h-10 bg-white" data-testid="chat-month-select"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            {months.map((m) => <SelectItem key={m} value={m} data-testid={`chat-month-${m}`}>{monthLabel(m)}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {data === null ? (
        <div className="text-slate-500">Loading…</div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-3">
            <Card className="p-4 border-slate-200" data-testid="chat-stat-sessions">
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Conversations</div>
              <div className="font-mono-num text-2xl font-extrabold text-slate-900 mt-1">{data.totals.sessions}</div>
            </Card>
            <Card className="p-4 border-slate-200" data-testid="chat-stat-questions">
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Customer Questions</div>
              <div className="font-mono-num text-2xl font-extrabold text-slate-900 mt-1">{data.totals.user_messages}</div>
            </Card>
            <Card className="p-4 border-slate-200" data-testid="chat-stat-messages">
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Total Messages</div>
              <div className="font-mono-num text-2xl font-extrabold text-slate-900 mt-1">{data.totals.messages}</div>
            </Card>
          </div>

          <Card className="p-5 border-slate-200" data-testid="chat-topics-card">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="w-4 h-4 text-emerald-700" />
              <h3 className="font-display font-bold">Sabse Zyada Puche Gaye Topics — {monthLabel(month)}</h3>
            </div>
            {topics.length === 0 ? (
              <p className="text-sm text-slate-400">Is mahine abhi koi chat nahi hui.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {topics.map((t, i) => (
                  <Badge key={t.word} data-testid={`chat-topic-${t.word}`}
                    className={`px-3 py-1.5 text-xs font-semibold ${i === 0 ? "bg-emerald-700 text-white hover:bg-emerald-700" : i < 3 ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-100" : "bg-slate-100 text-slate-600 hover:bg-slate-100"}`}>
                    {t.word} <span className="ml-1 opacity-70">×{t.count}</span>
                  </Badge>
                ))}
              </div>
            )}
          </Card>

          <div className="grid lg:grid-cols-5 gap-4 items-start">
            <Card className="lg:col-span-2 border-slate-200 overflow-hidden" data-testid="chat-session-list">
              <div className="px-4 py-3 border-b border-slate-100 bg-slate-50 text-xs font-bold uppercase tracking-wider text-slate-500">
                Conversations ({sessions.length})
              </div>
              <div className="max-h-[520px] overflow-y-auto divide-y divide-slate-100">
                {sessions.map((s) => (
                  <button key={s.session_id} onClick={() => setActive(s)} data-testid={`chat-session-${s.session_id}`}
                    className={`w-full text-left px-4 py-3 hover:bg-emerald-50/50 transition-colors ${active?.session_id === s.session_id ? "bg-emerald-50" : ""}`}>
                    <div className="text-xs font-mono text-slate-400 truncate">{s.session_id}</div>
                    <div className="text-sm text-slate-700 truncate mt-0.5">{s.preview}</div>
                    <div className="text-[11px] text-slate-400 mt-1">{s.msg_count} msgs • {s.last_at ? new Date(s.last_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}</div>
                  </button>
                ))}
                {sessions.length === 0 && <div className="px-4 py-10 text-center text-sm text-slate-400">Is mahine koi conversation nahi.</div>}
              </div>
            </Card>

            <Card className="lg:col-span-3 border-slate-200 overflow-hidden" data-testid="chat-thread-panel">
              <div className="px-4 py-3 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  {active ? "Conversation" : "Koi conversation chunein"}
                </span>
                {active && (
                  <div className="flex items-center gap-1">
                    <button onClick={() => { const i = sessions.findIndex((x) => x.session_id === active.session_id); if (i > 0) setActive(sessions[i - 1]); }}
                      data-testid="chat-thread-prev" className="p-1.5 rounded-lg hover:bg-slate-200 text-slate-500"><ChevronLeft className="w-4 h-4" /></button>
                    <button onClick={() => { const i = sessions.findIndex((x) => x.session_id === active.session_id); if (i < sessions.length - 1) setActive(sessions[i + 1]); }}
                      data-testid="chat-thread-next" className="p-1.5 rounded-lg hover:bg-slate-200 text-slate-500"><ChevronRight className="w-4 h-4" /></button>
                  </div>
                )}
              </div>
              <div className="max-h-[520px] overflow-y-auto p-4 space-y-2.5 bg-slate-50/50">
                {activeMsgs.map((m, i) => (
                  <div key={i} className={`flex items-start gap-2 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
                    <span className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${m.role === "user" ? "bg-emerald-700" : "bg-slate-300"}`}>
                      {m.role === "user" ? <User className="w-3.5 h-3.5 text-white" /> : <Headset className="w-3.5 h-3.5 text-slate-600" />}
                    </span>
                    <div className={`max-w-[80%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-line ${m.role === "user" ? "bg-emerald-700 text-white rounded-br-sm" : "bg-white border border-slate-200 text-slate-700 rounded-bl-sm"}`}>
                      {m.content}
                    </div>
                  </div>
                ))}
                {!active && <div className="py-16 text-center text-sm text-slate-400">Left se koi conversation click karein — poori chat yahan dikhegi.</div>}
              </div>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
